import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { cx } from './cx';
import { IconButton } from './Form';
import { Portal } from './Portal';
import './components.css';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  /** Buttons, right-aligned. */
  footer?: ReactNode;
  /** `bottom` on phones, `right` on desktop. Defaults to `bottom` under 720px. */
  side?: 'bottom' | 'right' | 'auto';
  /** Announced name when there is no visible title. */
  ariaLabel?: string;
  className?: string;
}

/**
 * A modal surface: bottom sheet on a phone, right drawer on a desktop. Escape
 * closes it, the page behind it stops scrolling, and focus lands inside.
 *
 * It renders through <Portal> into <body>, and it must: `.screen` carries
 * `z-index: 1`, which caps every descendant's stacking order, so a sheet
 * rendered in place paints *under* the app header and tab bar — title bar,
 * close button and footer buttons all unreachable. Three screens hit this
 * independently and each wrote its own portal before it was fixed here.
 */
export function Sheet({ open, onClose, title, children, footer, side = 'auto', ariaLabel, className }: SheetProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the panel so the next Tab lands inside it, not behind it.
    ref.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const resolved = side === 'auto'
    ? (typeof window !== 'undefined' && window.innerWidth >= 720 ? 'right' : 'bottom')
    : side;

  return (
    <Portal name="sheet">
      <div className="k-scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={ref}
        className={cx('k-sheet', `k-sheet--${resolved}`, className)}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : ariaLabel}
        tabIndex={-1}
      >
        {resolved === 'bottom' && <div className="k-sheet__grab" aria-hidden="true" />}
        {title != null && (
          <header className="k-sheet__head">
            <h2 className="k-sheet__title">{title}</h2>
            <IconButton label="Close" onClick={onClose}>✕</IconButton>
          </header>
        )}
        <div className="k-sheet__body">{children}</div>
        {footer != null && <footer className="k-sheet__foot">{footer}</footer>}
      </div>
    </Portal>
  );
}
